/*
 * Page Particuliers : chiffres clés, fonctionnement du partage (schéma), avantages (graphique avant / après),
 * parcours d'adhésion d'un particulier, options de contact, FAQ.
 * Textes : clés « particuliers.* » (fr) ; titres partagés avec la page Entreprises (« enterprise.* », « enterprisePage.* »).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, CheckCircle, ChevronDown, Clock, FileText, HeartHandshake, HelpCircle, Home, Leaf, PhoneCall,
  Settings, Shield, Sun, Target, TrendingDown, UserPlus, Users, Euro, Zap,
} from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { EnergySharingDiagram } from '../components/EnergySharingDiagram';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { joinSteps } from '../lib/joinSteps';

const ACCENT = '#F8BD3D';
const INK = '#1a1a1a';
const TEXT = '#4a4a4a';
const MUTED = '#666666';

type Benefit = { title: string; desc: string };
type FaqItem = { question: string; answer: string };

const CONSUMER_ICONS = [TrendingDown, Leaf, Shield, FileText];
const PRODUCER_ICONS = [Euro, Users, Target, Settings];

function SectionTitle({ icon: Icon, children }: { icon: typeof Sun; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
        <Icon className="w-6 h-6" style={{ color: ACCENT }} />
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: INK }}>{children}</h2>
    </div>
  );
}

const primaryBtn = 'inline-flex items-center gap-2 rounded-full font-semibold transition-all duration-300';

export default function ParticuliersPage() {
  const { t } = useTranslation();
  const [role, setRole] = useState<'consumer' | 'producer'>('consumer');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const list = <T,>(key: string): T[] => {
    const v = t(key, { returnObjects: true });
    return Array.isArray(v) ? (v as T[]) : [];
  };
  const benefits = list<Benefit>(role === 'consumer' ? 'particuliers.benefits.consumer' : 'particuliers.benefits.producer');
  const benefitIcons = role === 'consumer' ? CONSUMER_ICONS : PRODUCER_ICONS;
  const faq = list<FaqItem>('particuliers.faq.items');
  const steps = joinSteps(t, 'particulier');

  const ctaCards = [
    { icon: Clock, title: t('particuliers.cta.callTitle'), desc: t('particuliers.cta.callDesc'), label: t('particuliers.cta.callButton'), href: 'https://calendly.com/sunisup/15min' },
    { icon: PhoneCall, title: t('particuliers.cta.contactTitle'), desc: t('particuliers.cta.contactDesc'), label: t('particuliers.cta.contactButton'), to: '/contact' },
    { icon: UserPlus, title: t('particuliers.cta.joinTitle'), desc: t('particuliers.cta.joinDesc'), label: t('particuliers.cta.joinButton'), to: '/nous-rejoindre' },
  ];

  return (
    <>
      <SEOHead
        title={t('particuliers.seo.title')}
        description={t('particuliers.seo.description')}
        keywords={t('particuliers.seo.keywords')}
        url="https://sunisup.be/particuliers"
        logo="https://sunisup.be/images/logo.png"
      />

      <div className="font-sans bg-white">
        {/* 1. Hero */}
        <section className="relative pt-48 pb-14 sm:pb-22 overflow-hidden" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #000000 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          </div>
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ color: INK }}>{t('particuliers.hero.title')}</h1>
            <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto" style={{ color: TEXT }}>{t('particuliers.hero.subtitle')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/nous-rejoindre" className={`${primaryBtn} px-8 py-4 text-lg shadow-lg hover:shadow-2xl hover:-translate-y-1`} style={{ backgroundColor: ACCENT, color: INK }}>
                <UserPlus className="w-5 h-5" />{t('particuliers.hero.join')}
              </Link>
              <a href="#fonctionnement" className={`${primaryBtn} px-8 py-4 text-lg border bg-white hover:shadow-lg`} style={{ borderColor: '#e5e5e5', color: INK }}>
                {t('particuliers.hero.how')}<ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        {/* 2. Chiffres clés */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {[
                { icon: Zap, value: '1200+ MWh', label: t('enterprisePage.stats.shared') },
                { icon: Home, value: '220+', label: t('particuliers.stats.members') },
                { icon: Target, value: '5 GWh', label: t('enterprisePage.stats.goal') },
              ].map(s => (
                <div key={s.value}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                    <s.icon className="w-7 h-7" style={{ color: ACCENT }} />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold" style={{ color: INK }}>{s.value}</h3>
                  <p className="text-sm font-medium mt-1" style={{ color: MUTED }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. Comment fonctionne le partage (schéma) */}
        <section id="fonctionnement" className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa', scrollMarginTop: '96px' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle icon={Zap}>{t('enterprise.howItWorks.title')}</SectionTitle>
            <p className="text-lg mb-10 max-w-3xl" style={{ color: TEXT }}>{t('enterprise.howItWorks.subtitle')}</p>
            <EnergySharingDiagram />
            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {list<string>('particuliers.howItWorks.points').map(point => (
                <div key={point} className="flex items-start gap-3 p-4 rounded-xl bg-white" style={{ border: '1px solid #e5e5e5' }}>
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                  <p className="text-sm" style={{ color: INK }}>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Avantages du partage + graphique avant / après */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle icon={TrendingDown}>{t('enterprise.billing.title')}</SectionTitle>
            <p className="text-lg mb-10 max-w-3xl" style={{ color: TEXT }}>{t('particuliers.benefits.subtitle')}</p>

            <div className="flex justify-center mb-12">
              <div className="inline-flex rounded-full p-1" style={{ backgroundColor: '#f5f5f5' }}>
                {([
                  { key: 'consumer' as const, icon: Home, label: t('particuliers.benefits.consumeTab') },
                  { key: 'producer' as const, icon: Sun, label: t('particuliers.benefits.produceTab') },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setRole(tab.key)}
                    aria-pressed={role === tab.key}
                    className="px-4 sm:px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 text-sm sm:text-base"
                    style={{ backgroundColor: role === tab.key ? ACCENT : 'transparent', color: role === tab.key ? INK : MUTED }}
                  >
                    <tab.icon className="w-5 h-5" />{tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {benefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.title} className="p-6 rounded-2xl transition-all duration-300 hover:shadow-lg" style={{ backgroundColor: '#fafafa', border: '1px solid #e5e5e5' }}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                        <Icon className="w-6 h-6" style={{ color: ACCENT }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2" style={{ color: INK }}>{b.title}</h3>
                        <p style={{ color: MUTED }}>{b.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mb-10">
              <BeforeAfterSlider role={role} />
            </div>

            <div className="text-center">
              <Link to="/nous-rejoindre" className={`${primaryBtn} px-8 py-4 shadow-lg hover:shadow-2xl hover:-translate-y-1`} style={{ backgroundColor: ACCENT, color: INK }}>
                {t('particuliers.benefits.cta')}<ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* 5. Parcours d'un particulier (identique à la page « Nous rejoindre ») */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle icon={HeartHandshake}>{t('particuliers.parcours.title')}</SectionTitle>
            <p className="text-lg mb-12 max-w-3xl" style={{ color: TEXT }}>{t('particuliers.parcours.subtitle')}</p>

            <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {steps.map((step, i) => (
                <li key={step.title} className="p-6 rounded-2xl bg-white" style={{ border: '1px solid #e5e5e5' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ACCENT }}>
                      <span className="text-white font-bold">{i + 1}</span>
                    </div>
                    <step.icon className="w-5 h-5" style={{ color: ACCENT }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: INK }}>{step.title}</h3>
                  {step.lines.map(line => <p key={line} className="text-sm leading-relaxed mb-1.5" style={{ color: MUTED }}>{line}</p>)}
                  {step.list && (
                    <ul className="mt-1 space-y-0.5 text-sm list-disc pl-5" style={{ color: MUTED }}>
                      {step.list.map(item => <li key={item}>{item}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ol>

            <div className="mt-10 text-center">
              <Link to="/nous-rejoindre" className={`${primaryBtn} px-8 py-4 shadow-lg hover:shadow-2xl hover:-translate-y-1`} style={{ backgroundColor: ACCENT, color: INK }}>
                <UserPlus className="w-5 h-5" />{t('particuliers.parcours.join')}
              </Link>
            </div>
          </div>
        </section>

        {/* 6. Options de contact */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: INK }}>{t('particuliers.cta.title')}</h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: TEXT }}>{t('particuliers.cta.subtitle')}</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {ctaCards.map(card => {
                const inner = <><card.icon className="w-4 h-4" />{card.label}</>;
                const cls = `${primaryBtn} px-5 py-3 whitespace-nowrap hover:shadow-lg`;
                return (
                  <div key={card.title} className="p-6 rounded-2xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                      <card.icon className="w-7 h-7" style={{ color: ACCENT }} />
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: INK }}>{card.title}</h3>
                    <p className="text-sm mb-6" style={{ color: MUTED }}>{card.desc}</p>
                    {card.href
                      ? <a href={card.href} target="_blank" rel="noopener noreferrer" className={cls} style={{ backgroundColor: ACCENT, color: INK }}>{inner}</a>
                      : <Link to={card.to!} className={cls} style={{ backgroundColor: ACCENT, color: INK }}>{inner}</Link>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 7. FAQ */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle icon={HelpCircle}>{t('particuliers.faq.title')}</SectionTitle>
            <div className="mt-8 space-y-3">
              {faq.map((item, i) => {
                const open = openFaq === i;
                return (
                  <div key={item.question} className="rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid #e5e5e5' }}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      aria-expanded={open}
                      aria-controls={`faq-${i}`}
                      className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                    >
                      <span className="font-semibold" style={{ color: INK }}>{item.question}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: ACCENT }} />
                    </button>
                    {open && (
                      <p id={`faq-${i}`} className="px-5 sm:px-6 pb-5 leading-relaxed" style={{ color: TEXT }}>{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 8. Appel final */}
        <section className="py-14 sm:py-22" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.3)' }}>
              <Sun className="w-10 h-10" style={{ color: ACCENT }} />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6" style={{ color: INK }}>{t('particuliers.finalCta.title')}</h2>
            <p className="text-xl mb-8" style={{ color: TEXT }}>{t('particuliers.finalCta.text')}</p>
            <Link to="/nous-rejoindre" className={`${primaryBtn} px-8 py-4 text-lg shadow-lg hover:shadow-2xl hover:-translate-y-1`} style={{ backgroundColor: ACCENT, color: INK }}>
              {t('particuliers.finalCta.button')}<ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
